/**
 * Header Component
 * 顯示標題、商品類型選擇器和語言切換
 * Requirements: 1.1, 1.2
 */

import { GlobeAltIcon } from '@heroicons/react/24/outline';
import type { CategoryType } from '../types/card';
import type { Language, Translations } from '../i18n';
import { languageNames } from '../i18n';

interface HeaderProps {
  category: CategoryType;
  onCategoryChange: (category: CategoryType) => void;
  language: Language;
  onLanguageChange: (lang: Language) => void;
  t: Translations;
}

export function Header({ 
  category, 
  onCategoryChange, 
  language, 
  onLanguageChange,
  t 
}: HeaderProps) {
  const categoryLabels: Record<CategoryType, string> = {
    vps: t.categoryVps,
    vds: t.categoryVds,
    hosting: t.categoryHosting,
    account: t.categoryAccount,
    domain: t.categoryDomain,
  };

  const languages: Language[] = ['zh-TW', 'zh-CN', 'en'];

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{t.appTitle}</h1>
          <p className="text-sm text-gray-500">{t.appSubtitle}</p>
        </div>
        
        {/* 語言切換 */}
        <div className="relative">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <GlobeAltIcon className="w-4 h-4 text-gray-500 ml-2" />
            <select
              value={language}
              onChange={(e) => onLanguageChange(e.target.value as Language)}
              className="bg-transparent text-sm font-medium text-gray-700 pr-2 py-1 focus:outline-none cursor-pointer"
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {languageNames[lang]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {(Object.keys(categoryLabels) as CategoryType[]).map((cat) => (
          <button
            key={cat}
            onClick={() => onCategoryChange(cat)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              category === cat
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {categoryLabels[cat]}
          </button>
        ))}
      </div>
    </div>
  );
}
